import React, { useEffect, useMemo, useState } from 'react';
import { aiAgentApi } from '../../lib/aiAgentApi';
import { toast } from 'sonner';
import { Building2, Search, Copy, Check, Eye, Mail, MessageSquare, Clock, X, AlertTriangle, FolderOpen, ArrowLeft } from 'lucide-react';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const fmtRelative = (iso) => {
  if (!iso) return 'Never';
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    if (diff < 86400 * 30) return `${Math.round(diff / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return iso; }
};

const CopyChip = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy');
    }
  };
  if (!value) return <span className="text-slate-400">—</span>;
  return (
    <button
      onClick={copy}
      className="inline-flex items-center gap-1.5 rounded-md bg-slate-50 px-2 py-0.5 font-mono text-[11px] text-slate-700 hover:bg-slate-100"
      title={`Copy ${label}`}
    >
      <span className="truncate max-w-[260px]">{value}</span>
      {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3 text-slate-400" />}
    </button>
  );
};

// ----- Agent Viewer Drawer (read-only, super-admin view) -----

const AgentViewerDrawer = ({ agent, onClose }) => {
  const [view, setView] = useState('chats'); // chats | email | drive
  const [convs, setConvs] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [totalMessages, setTotalMessages] = useState(0);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [emails, setEmails] = useState([]);
  const [emailCounts, setEmailCounts] = useState({});
  const [emailView, setEmailView] = useState('all');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!agent?.user_id) return;
    setLoading(true);
    (async () => {
      try {
        if (view === 'chats') {
          const d = await aiAgentApi.adminAgentConversations(agent.user_id);
          setConvs(d.conversations || []);
        } else if (view === 'email') {
          const d = await aiAgentApi.adminAgentEmail(agent.user_id, { view: emailView });
          setEmails(d.emails || []);
          setEmailCounts(d.counts || {});
        } else if (view === 'drive') {
          const d = await aiAgentApi.adminAgentDrive(agent.user_id);
          setFiles(d.files || []);
        }
      } catch { toast.error('Could not load agent data'); }
      finally { setLoading(false); }
    })();
  }, [agent?.user_id, view, emailView]);

  const openConv = async (c) => {
    setActiveConv(c);
    try {
      const detail = await aiAgentApi.adminAgentConversationDetail(agent.user_id, c.id, { limit: 5, include_tool: false });
      setMessages(detail.messages || []);
      setHasMore(Boolean(detail.has_more));
      setTotalMessages(detail.total_messages || 0);
    } catch { toast.error('Could not open chat'); }
  };

  const loadOlder = async () => {
    if (!activeConv || !hasMore || loadingOlder || messages.length === 0) return;
    setLoadingOlder(true);
    try {
      const detail = await aiAgentApi.adminAgentConversationDetail(agent.user_id, activeConv.id, {
        limit: 10, before_ts: messages[0].ts, include_tool: false,
      });
      const older = detail.messages || [];
      const seen = new Set(messages.map(m => m.id));
      setMessages([...older.filter(m => !seen.has(m.id)), ...messages]);
      setHasMore(Boolean(detail.has_more));
    } catch { toast.error('Could not load earlier'); }
    finally { setLoadingOlder(false); }
  };

  if (!agent) return null;

  return (
    <div
      data-testid="ai-agent-viewer-drawer"
      className="fixed inset-0 z-[60] flex justify-end bg-slate-900/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="h-full w-screen sm:max-w-none bg-white shadow-2xl flex flex-col"
        style={{ width: 'min(75vw, 1280px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="h-7 w-7 shrink-0 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 ring-2 ring-emerald-100" />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <div className="text-sm font-semibold text-slate-900 truncate">{agent.agent_first_name || agent.user_name}</div>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600">AI Agent</span>
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">Admin view · read-only</span>
              </div>
              <div className="mt-0.5 text-[11px] text-slate-500 truncate">
                {agent.agent_email} · {agent.user_email}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Sub-tabs */}
        <div className="border-b border-slate-100 bg-slate-50 px-3">
          <div className="flex items-center gap-1">
            {[
              { id: 'chats', label: 'Chats', Icon: MessageSquare },
              { id: 'email', label: 'Email', Icon: Mail },
              { id: 'drive', label: 'Drive', Icon: FolderOpen },
            ].map(({ id, label, Icon }) => (
              <button
                key={id}
                data-testid={`ai-viewer-tab-${id}`}
                onClick={() => { setView(id); setActiveConv(null); }}
                className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-semibold transition ${view === id ? 'border-emerald-500 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-900'}`}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {loading && <div className="py-10 text-center text-xs text-slate-400">Loading…</div>}

          {!loading && view === 'chats' && (
            <div className="h-full">
              {activeConv ? (
                <div className="flex h-full flex-col">
                  <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
                    <button onClick={() => setActiveConv(null)} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100">
                      <ArrowLeft className="h-4 w-4" />
                    </button>
                    <div className="text-sm font-semibold text-slate-900 truncate">{activeConv.title || 'Untitled chat'}</div>
                    <span className="ml-auto text-[10px] text-slate-400">{totalMessages} total messages</span>
                  </div>
                  {hasMore && (
                    <div className="border-b border-slate-100 bg-white px-4 py-1.5 text-center">
                      <button onClick={loadOlder} disabled={loadingOlder}
                        className="rounded-md border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40">
                        {loadingOlder ? 'Loading…' : `Load earlier (${Math.max(0, totalMessages - messages.length)} remaining)`}
                      </button>
                    </div>
                  )}
                  <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                    {messages.map((m) => (
                      <div key={m.id} className={m.role === 'user' ? 'text-right' : 'text-left'}>
                        <div className="text-[10px] uppercase font-semibold tracking-wide text-slate-400 mb-0.5">{m.role}</div>
                        <div className={`inline-block max-w-[80%] rounded-xl px-3 py-2 text-sm ${m.role === 'user' ? 'bg-emerald-50 text-slate-900' : 'bg-slate-100 text-slate-900'}`}>
                          <div className="whitespace-pre-wrap">{m.content}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <ul className="space-y-2 px-3 py-3">
                  {convs.length === 0 && <li className="py-8 text-center text-xs text-slate-400">No chats yet.</li>}
                  {convs.map((c) => (
                    <li key={c.id} data-testid={`ai-viewer-conv-${c.id}`}
                      onClick={() => openConv(c)}
                      className="cursor-pointer rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-emerald-200 hover:shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-semibold text-slate-900">{c.title || 'Untitled chat'}</div>
                          {c.last_snippet && (
                            <div className="mt-0.5 truncate text-xs text-slate-500">
                              <span className="font-medium text-slate-400">{c.last_snippet_role === 'assistant' ? 'Agent: ' : 'User: '}</span>
                              {c.last_snippet}
                            </div>
                          )}
                        </div>
                        <div className="text-[11px] whitespace-nowrap text-slate-400">{fmtRelative(c.last_active_at)}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {!loading && view === 'email' && (
            <div className="px-3 py-3">
              <div className="flex flex-wrap gap-1 mb-3">
                {['all', 'inbox', 'sent', 'approvals', 'failed'].map((v) => (
                  <button key={v} onClick={() => setEmailView(v)}
                    className={`rounded-md px-2.5 py-1 text-[11px] font-semibold ${emailView === v ? 'bg-emerald-600 text-white' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                    {v}{emailCounts[v] ? ` (${emailCounts[v]})` : ''}
                  </button>
                ))}
              </div>
              {emails.length === 0 ? (
                <div className="py-8 text-center text-xs text-slate-400">No emails.</div>
              ) : (
                <ul className="space-y-2">
                  {emails.map((e) => (
                    <li key={e.id} className="rounded-xl border border-slate-200 bg-white px-4 py-2.5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <div className="truncate text-sm font-semibold text-slate-900">{e.subject || '(no subject)'}</div>
                            {e.ai_drafted && <span className="rounded-full bg-violet-100 px-1.5 text-[10px] font-semibold text-violet-700">AI</span>}
                          </div>
                          <div className="mt-0.5 truncate text-xs text-slate-500">{e.direction === 'inbound' ? `From: ${e.reply_to || '—'}` : `To: ${(e.to || []).join(', ')}`}</div>
                        </div>
                        <div className="flex flex-col items-end gap-0.5 text-[10px] text-slate-400">
                          <span className="rounded-full bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-700">{e.status}</span>
                          <span>{fmtRelative(e.updated_at || e.created_at)}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {!loading && view === 'drive' && (
            <div className="px-3 py-3">
              {files.length === 0 ? (
                <div className="py-8 text-center text-xs text-slate-400">No files.</div>
              ) : (
                <ul className="space-y-2">
                  {files.map((f) => (
                    <li key={f.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <FolderOpen className="h-4 w-4 text-slate-400 shrink-0" />
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-slate-900">{f.filename}</div>
                          <div className="text-[10px] text-slate-500">{Math.round((f.size_bytes || 0) / 1024)} KB · {fmtRelative(f.created_at)} · {f.visibility}</div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ----- Main Agent List Tab -----

export const AgentListTab = ({ orgId }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const d = await aiAgentApi.adminAgents({ q: search || undefined, org_id: orgId || undefined });
        if (cancelled) return;
        setAgents(d.agents || []);
      } catch { if (!cancelled) toast.error('Could not load agents'); }
      finally { if (!cancelled) setLoading(false); }
    }, 250); // debounce search
    return () => { cancelled = true; clearTimeout(t); };
  }, [search, orgId]);

  const totalAgents = agents.length;
  const inactiveCount = useMemo(() => agents.filter(a => a.user_status === 'inactive').length, [agents]);

  return (
    <div data-testid="ai-mgr-agents-tab">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="ai-mgr-agents-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by agent name, user email or org…"
            className="w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-8 pr-3 text-sm outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
          />
        </div>
        <div className="text-xs text-slate-500">
          {totalAgents} agent{totalAgents === 1 ? '' : 's'}
          {inactiveCount > 0 && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-rose-700"><AlertTriangle className="h-3 w-3" />{inactiveCount} inactive</span>}
        </div>
      </div>

      {loading ? (
        <div className="py-10 text-center text-sm text-slate-400">Loading agents…</div>
      ) : agents.length === 0 ? (
        <div className="py-10 text-center text-sm text-slate-400">No agents match.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Agent</th>
                <th className="text-left px-3 py-2">Agent email</th>
                <th className="text-left px-3 py-2">Assigned to user</th>
                <th className="text-left px-3 py-2">Org(s)</th>
                <th className="text-right px-3 py-2">Threads</th>
                <th className="text-right px-3 py-2">Sent</th>
                <th className="text-right px-3 py-2">Pending</th>
                <th className="text-left px-3 py-2">Last active</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => (
                <tr key={a.user_id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="h-6 w-6 shrink-0 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600" />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900">{a.agent_first_name || '—'}</div>
                        <div className="text-[10px] text-slate-500">{a.agent_full_name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2"><CopyChip value={a.agent_email} label="agent email" /></td>
                  <td className="px-3 py-2">
                    <div className="text-sm text-slate-900 truncate max-w-[200px]">{a.user_name || a.user_email}</div>
                    <div className="text-[10px] text-slate-500 truncate max-w-[200px]">{a.user_email}</div>
                    {a.user_status === 'inactive' && (
                      <span className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700">
                        <AlertTriangle className="h-2.5 w-2.5" /> User inactive
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1 max-w-[260px]">
                      {(a.orgs || []).slice(0, 3).map((o) => (
                        <span key={o.id} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-700">
                          <Building2 className="h-2.5 w-2.5" />{o.name}
                        </span>
                      ))}
                      {(a.orgs || []).length > 3 && (
                        <span className="text-[10px] text-slate-400">+{a.orgs.length - 3}</span>
                      )}
                      {(a.orgs || []).length === 0 && <span className="text-[10px] text-slate-400">—</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">{a.thread_count.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">{a.email_sent_count.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">
                    {a.email_pending_count > 0
                      ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-amber-700">{a.email_pending_count}</span>
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-slate-500 whitespace-nowrap"><Clock className="inline h-3 w-3 mr-1" />{fmtRelative(a.last_active_at)}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      data-testid={`ai-mgr-agent-view-${a.user_id}`}
                      onClick={() => setViewing(a)}
                      className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700"
                    >
                      <Eye className="h-3 w-3" /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewing && <AgentViewerDrawer agent={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
};
